import React, {CSSProperties, Dispatch, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  ActionType,
  AssetMetaInterface,
  ComputedPageItem,
} from '@taopix/taopix-core/dist/types/types';
import {
  Checkbox,
  ContentPanel,
  HelpButton,
  RadioButton,
  SlidersIcon,
} from '@taopix/taopix-design-system';
import {RangeWithValue} from '@taopix/taopix-design-system';
import {
  AssetType,
  BoxType,
  Brightness,
  Contrast,
  BoxLayer,
  NumBool,
  AssetPart,
  CoreActions,
  AssetStatus,
} from '@taopix/taopix-core';
import {useTaopix} from '../../../Context/TaopixContext';
import classNames from 'classnames';
import {useExperience} from '../../../Experience/ExperienceProvider';
import {useUiState} from '../../../Context/UserInterface/UserInterfaceContext';
import {UserInterfaceActions} from '../../../Context/UserInterface/UserInterfaceActionTypes';
import useStatusChecker from '../../../../Hooks/Assets/useStatusChecker';
import {useDispatch} from 'react-redux';
import ToolPaletteSection from '../../../ToolPalette/ToolPaletteSection';
import useSettings from '../../../../Hooks/useSettings';
import {AssetWorkflow, flipSize} from '@taopix/taopix-asset-manager';
import PaletteInformationMessage from '../PaletteInformationMessage';
import {calcRotation} from '../../../../Utils/Asset';

export type EnhanceProps = {
  sectionKey: string;
  item?: ComputedPageItem;
  assets: AssetMetaInterface[];
};

type EnhancePictures = {
  imageperfectlyclearenhance: boolean;
  pictureBrightness: number;
  pictureContrast: number;
  imageColourMode: number;
};

type EnhanceExperienceValues = {
  autoEnhance: boolean;
  brightness: boolean;
  contrast: boolean;
  filter: boolean;
};

const Enhance = ({sectionKey, item, assets}: EnhanceProps) => {
  const app = useTaopix();
  const ui = useUiState();
  const settings = useSettings();
  const {t} = useTranslation();

  const monitor = useStatusChecker(AssetWorkflow.ENHANCE);
  const {experience} = useExperience();
  const {pictureBoxOptions, backgroundOptions, stickerOptions} = experience.EDITOR;

  const dispatch = useDispatch<Dispatch<ActionType<typeof CoreActions>>>();
  const deepClone = app.core.get('utils').deepClone;
  const isEnhanced = app.core.get('utils').isEnhanced;

  const [showSection, setShowSection] = useState<boolean>(false);
  const [disableSection, setDisableSection] = useState<boolean>(false);
  const [notAvailableMessage, setNotAvailableMessage] = useState<string>('');

  const [disableEnhance, setDisableEnhance] = useState<boolean>(true);

  const [enhancePictureValues, setEnhancePictureValues] = useState<EnhancePictures>({
    imageperfectlyclearenhance: Boolean(item?.imageperfectlyclearenhance),
    pictureBrightness: item?.imagebrightness as number,
    pictureContrast: item?.imagecontrast as number,
    imageColourMode: item?.imagecolourmode as number,
  });

  const [enhanceOptions, setEnhanceOptions] = useState<EnhanceExperienceValues>({
    autoEnhance: true,
    brightness: true,
    contrast: true,
    filter: true,
  });

  useEffect(() => {
    setEnhancePictureValues({
      imageperfectlyclearenhance: Boolean(item?.imageperfectlyclearenhance),
      pictureBrightness: item?.imagebrightness as number,
      pictureContrast: item?.imagecontrast as number,
      imageColourMode: item?.imagecolourmode as number,
    });
  }, [
    item?.imageperfectlyclearenhance,
    item?.imagecolourmode,
    item?.imagebrightness,
    item?.imagecontrast,
  ]);

  // if the image is not processed, ignore rotation
  const rotation = useMemo(() => {
    if (!item?.asset) {
      return 0;
    }
    return calcRotation(item?.asset);
  }, [item?.asset]);

  const rotatedStyles = useMemo(() => {
    return {
      transform: `rotate(${rotation}deg)`,
    };
  }, [rotation]) as CSSProperties;

  const sizing = useMemo(() => {
    if (!item?.asset) {
      return {
        height: 0,
        width: 0,
      };
    }
    return flipSize(rotation, {width: item?.asset?.width, height: item?.asset?.height});
  }, [item?.asset, rotation]);

  const cssProperties = {
    '--asset-width': sizing.width,
    '--asset-height': sizing.height,
  } as React.CSSProperties;

  const isActiveInExperience = (item: ComputedPageItem) => {
    const boxType = item.type;
    // Return whether or not this option is enabled in the experience for the selected item
    if (
      boxType === BoxType.Image &&
      item?.asset?.assetType !== AssetType.Background &&
      item?.asset?.assetType !== AssetType.ScrapbookImage &&
      item?.hirespath !== ''
    ) {
      setEnhanceOptions({
        autoEnhance: pictureBoxOptions.autoEnhance,
        brightness: pictureBoxOptions.brightness,
        contrast: pictureBoxOptions.contrast,
        filter: pictureBoxOptions.filter,
      });
      return pictureBoxOptions.enhance;
    }
    if (boxType === BoxType.Image && item?.layer === BoxLayer.Background) {
      setEnhanceOptions({
        autoEnhance: false,
        brightness: backgroundOptions.brightness,
        contrast: backgroundOptions.contrast,
        filter: backgroundOptions.filter,
      });
      return backgroundOptions.enhance;
    }
    if (AssetType.ScrapbookImage === item.hiresfiletype) {
      setEnhanceOptions({
        autoEnhance: false,
        brightness: stickerOptions.brightness,
        contrast: stickerOptions.contrast,
        filter: stickerOptions.filter,
      });
      return stickerOptions.enhance;
    } else {
      return false;
    }
  };

  // Check if we can show this option based on the selected item type, its protection, the experience and canEditPageLayout
  useEffect(() => {
    if (item && item.ref !== -1) {
      // If there is a selected item
      if (isActiveInExperience(item)) {
        // Show the button
        setShowSection(true);
        // Set whether the button should be disabled or not
        setDisableSection(
          NumBool.True === item.protectcontents ||
            (item.errors?.imageMissing as boolean) ||
            ui.state.swapActive
        );
      } else {
        // If this palette is not enabled for the selected item, hide the toolpalette button and show the 'not available for the selected item' message
        setShowSection(false);
        setNotAvailableMessage(t('toolpalette:str_OptionNotAvailableForSelectedItem'));
      }
    } else {
      // If no item is selected, hide the button and show the 'please select an item' message
      setShowSection(false);
      setNotAvailableMessage(t('toolpalette:str_MessageSelectAnItemToViewOptions'));
    }
  }, [
    item?.asset,
    item?.asset?.assetType,
    item?.type,
    item?.image,
    item?.shape,
    ui.state.swapActive,
  ]);

  const onEnhanceImage = (assetPart: AssetPart) => {
    // if we already have the enhanced fpos just set the imageperfectlyclearenhance otherwise call module to get them
    if (item?.asset && item.asset.generatedParts && isEnhanced(item.asset.generatedParts)) {
      app.core
        .dispatch('SHAPE.ENHANCE_IMAGE', {
          enhance: true,
        })
        .catch(error => {
          console.error(error);
        });
      return;
    }

    ui.dispatch(UserInterfaceActions.setIsBusy(true));
    app.core
      .get('stateManager')
      .call('enhanceImage', {
        item: item,
        jobType: assetPart.toString(),
        scaled: false,
      })
      .then(response => {
        monitor?.add(item?.asset?.id as number);
      })
      .catch(error => {
        console.error(error);
        ui.dispatch(UserInterfaceActions.setIsBusy(false));
      });
  };

  const onUpdate = useCallback(
    (event: CustomEvent<Partial<any>[]>) => {
      const updates = event.detail;
      const enhancedImg = updates[0];

      const asset = assets.find(a => Number(a.id) === Number(updates[0].id));
      if (typeof asset === 'undefined') return;

      const enhancedAsset = {
        ...deepClone(asset),
        ...enhancedImg,
      };

      // update the asset to enahnced version
      dispatch(
        CoreActions.updateAsset({
          ...(enhancedAsset as unknown as AssetMetaInterface),
        })
      );

      app.core
        .dispatch('SHAPE.ENHANCE_IMAGE', {
          enhance: true,
        })
        .catch(error => {
          console.error(error);
        });

      ui.dispatch(UserInterfaceActions.setIsBusy(false));
    },
    [assets]
  );

  useEffect(() => {
    monitor?.on('update', onUpdate);
    return () => {
      monitor?.off('update', onUpdate);
    };
  }, [onUpdate, monitor]);

  /*
  *To Review* - Not sure if there is a better way?
    There is small delay between actually uploading an image and when the image comes back from status checker with generated parts.
    Clicking the checkbox during that time causes an error, so it is disabled until the upload status has finished and there are
    generated parts.
  */
  // check if the generated parts has been added to picture before undisabling checkbox
  useEffect(() => {
    const selectedAsset = assets.find(a => a.id === item?.asset?.id);
    if (
      selectedAsset &&
      selectedAsset.generatedParts &&
      selectedAsset.generatedParts !== '' &&
      AssetStatus.Processed === selectedAsset.status
    ) {
      setDisableEnhance(false);
      return;
    }
    setDisableEnhance(true);
  }, [assets, item]);

  const sectionButtonClasses = classNames('lg:py-md');

  const wrapperClasses = classNames(
    'flex',
    'flex-col',
    'px-md',
    'space-y-sm',
    'lg:flex-1',
    'sm:pb-md',
    'sm:h-mobilePaletteContentHeight',
    'sm:max-w-full',
    'overflow-y-auto'
  );

  if (
    ui.state.palette.section !== 'box-settings-palette' &&
    ui.state.palette.section !== `box-settings-palette/${sectionKey}`
  ) {
    return null;
  }

  return (
    <ToolPaletteSection
      id={'enhancePalette'}
      title={t('boxpalettes:str_TitleEnhance')}
      buttonIcon={<SlidersIcon />}
      sectionKey={sectionKey}
      buttonClasses={sectionButtonClasses}
      useFocusMode
      subSection={true}
      disabled={disableSection}
      showButton={showSection}
      buttonLayout="horizontal"
      parentselected={true}
    >
      <div className={wrapperClasses} tabIndex={-1}>
        {!showSection || disableSection ? (
          // If the palette is not allowed by the experience, or is disabled by the box protection, show the relevant message
          // We show this message rather than closing/hiding the palette to avoid abrupt UI changes when no item or an invalid item is selected
          <PaletteInformationMessage message={notAvailableMessage} />
        ) : (
          // Only show the sub options if the palette is allowed in the experience and the content is not protected
          <>
            {enhanceOptions.autoEnhance &&
              settings.state.features.hasPerfectlyClear &&
              !experience.SETTINGS.autoImageEnhancement.enhanceAllUserImagesAuto && (
                <ContentPanel className={'flex justify-between items-center'}>
                  <Checkbox
                    data-testid={'box-settings-enhance-pictures-checkbox'}
                    key={`enhance-pictures-${item?.ref}`}
                    id={'enhance_picture'}
                    label={
                      settings.state.features.displayPerfectlyClearName
                        ? t('*:str_MessageAutoEnhancePicturesWithPefectlyClear')
                        : t('*:str_MessageAutoEnhancePictures')
                    }
                    defaultChecked={item?.imageperfectlyclearenhance === NumBool.True}
                    disabled={disableEnhance}
                    onChange={(checked: boolean) => {
                      if (checked) {
                        onEnhanceImage(settings.state.features.perfectlyClearEnhanceMode);
                      } else {
                        app.core
                          .dispatch('SHAPE.ENHANCE_IMAGE', {
                            enhance: false,
                          })
                          .catch(error => {
                            console.error(error);
                          });
                      }
                    }}
                  />
                  <HelpButton
                    data-testid={'box-settings-auto-enhance-helpbutton'}
                    toolTipLabel={t('*:str_MessageWhatIsAutoEnhance')}
                    id={'perfectlyClearHelpTip'}
                    buttonStyle={'standard'}
                    boxless
                    placement={'auto'}
                    className={'self-start'}
                    isSmallScreen={ui.state.isSmallScreen}
                  />
                </ContentPanel>
              )}
            {enhanceOptions.brightness && (
              <ContentPanel>
                <RangeWithValue
                  data-testid={'box-settings-enhance-brightness-slider'}
                  key={`picture-brightness-${item?.ref}`}
                  value={enhancePictureValues.pictureBrightness}
                  min={Brightness.Min}
                  max={Brightness.Max}
                  label={t('boxpalettes:str_TitleBrightness')}
                  submitOnBlur={true}
                  onValueChange={(value: number, interacting?: boolean) => {
                    if (item?.ref && item?.ref !== -1) {
                      app.core
                        .dispatch('SHAPE.UPDATE_BRIGHTNESS', Number(value), interacting)
                        .catch(error => {
                          console.error(error);
                        });
                    }
                  }}
                  snapToValues={[0]}
                  showTickMarks
                  allowDecimal={false}
                  numberFieldSize={4}
                  isSmallScreen={ui.state.isSmallScreen}
                />
              </ContentPanel>
            )}
            {enhanceOptions.contrast && (
              <ContentPanel>
                <RangeWithValue
                  data-testid={'box-settings-enhance-contrast-slider'}
                  key={`picture-contrast-${item?.ref}`}
                  value={enhancePictureValues.pictureContrast}
                  min={Contrast.Min}
                  max={Contrast.Max}
                  id={'contrast'}
                  label={t('boxpalettes:str_TitleContrast')}
                  submitOnBlur={true}
                  onValueChange={(value: number, interacting?: boolean) => {
                    if (item?.ref && item?.ref !== -1) {
                      app.core
                        .dispatch('SHAPE.UPDATE_CONTRAST', Number(value), interacting)
                        .catch(error => {
                          console.error(error);
                        });
                    }
                  }}
                  snapToValues={[0]}
                  showTickMarks
                  allowDecimal={false}
                  numberFieldSize={4}
                  isSmallScreen={ui.state.isSmallScreen}
                />
              </ContentPanel>
            )}
            {enhanceOptions.filter && (
              <ContentPanel heading={t('boxpalettes:str_TitleFilter')}>
                <div className={'flex flex-col space-y-sm'}>
                  <RadioButton
                    data-testid={'box-settings-enhance-filternone-radio'}
                    groupName={'filters'}
                    key={`filter_picture_none-${item?.ref}`}
                    id={'filter_picture_none'}
                    label={t('*:str_LabelNone')}
                    // appearance={'border'}
                    defaultChecked={item?.imagecolourmode === 0}
                    onChange={() => {
                      app.core
                        .dispatch('SHAPE.UPDATE_IMAGE_EFFECT', {imagecolourmode: 0})
                        .catch(error => {
                          console.error(error);
                        });
                      setEnhancePictureValues({
                        ...enhancePictureValues,
                        imageColourMode: 0,
                      });
                    }}
                  >
                    <div
                      className={'flex relative w-[80rem] h-[80rem] shrink-0 mr-xs'}
                      style={cssProperties}
                    >
                      <img
                        style={rotatedStyles}
                        draggable={true}
                        src={item?.asset?.src}
                        alt={'filter_picture_none'}
                        onError={e => {
                          (e.target as HTMLImageElement).classList.add('hidden');
                          console.error(e);
                        }}
                        className={'z-[-1] object-cover'}
                      />
                    </div>
                    <span>{t('*:str_LabelNone')}</span>
                  </RadioButton>
                  <RadioButton
                    data-testid={'box-settings-enhance-filterblackandwhite-radio'}
                    groupName={'filters'}
                    key={`filter_picture_black_white-${item?.ref}`}
                    id={'filter_picture_black_white'}
                    label={t('*:str_LabelBlackAndWhite')}
                    // appearance={'border'}
                    defaultChecked={item?.imagecolourmode === 1}
                    onChange={() => {
                      app.core
                        .dispatch('SHAPE.UPDATE_IMAGE_EFFECT', {imagecolourmode: 1})
                        .catch(error => {
                          console.error(error);
                        });
                      setEnhancePictureValues({
                        ...enhancePictureValues,
                        imageColourMode: 1,
                      });
                    }}
                  >
                    <div
                      className={'flex relative w-[80rem] h-[80rem] z-[-1] shrink-0 mr-xs'}
                      style={cssProperties}
                    >
                      <img
                        style={rotatedStyles}
                        className={'grayscale z-[-1] object-cover'}
                        draggable={true}
                        src={item?.asset?.src}
                        alt={'filter_picture_black_white'}
                        onError={e => {
                          (e.target as HTMLImageElement).classList.add('hidden');
                          console.error(e);
                        }}
                      />
                    </div>
                    <span>{t('*:str_LabelBlackAndWhite')}</span>
                  </RadioButton>
                  <RadioButton
                    data-testid={'box-settings-enhance-filtersepia-radio'}
                    groupName={'filters'}
                    key={`filter_picture_sepia-${item?.ref}`}
                    id={'filter_picture_sepia'}
                    label={t('*:str_LabelSepia')}
                    // appearance={'border'}
                    defaultChecked={item?.imagecolourmode === 2}
                    onChange={() => {
                      app.core
                        .dispatch('SHAPE.UPDATE_IMAGE_EFFECT', {imagecolourmode: 2})
                        .catch(error => {
                          console.error(error);
                        });
                      setEnhancePictureValues({
                        ...enhancePictureValues,
                        imageColourMode: 2,
                      });
                    }}
                  >
                    <div
                      className={'flex relative w-[80rem] h-[80rem] z-[-1] shrink-0 mr-xs'}
                      style={cssProperties}
                    >
                      <img
                        style={rotatedStyles}
                        className={'sepia z-[-1] object-cover'}
                        draggable={true}
                        src={item?.asset?.src}
                        alt={'filter_picture_sepia'}
                        onError={e => {
                          (e.target as HTMLImageElement).classList.add('hidden');
                          console.error(e);
                        }}
                      />
                    </div>
                    <span>{t('*:str_LabelSepia')}</span>
                  </RadioButton>
                </div>
              </ContentPanel>
            )}
          </>
        )}
      </div>
    </ToolPaletteSection>
  );
};

export default Enhance;
